import { makeAutoObservable, runInAction } from 'mobx';
import { EmfitAPI } from '../services/BaseAPI';
import { STATUS_API } from 'src/config';

/**
 * A row of sb_group_devices. `id` is the PAIR id - it is what
 * DELETE /v1/group-device/:id expects, not the device id.
 */
export interface GroupDevicePair {
  id: number;
  group_id: number;
  device_id: string;
  details?: {
    device_id: string;
    device_name?: string;
    serial_number?: string;
  };
}

export interface Group {
  id: number;
  name: string;
  daily_report_time: string;
  created_at: Date;
  updated_at: Date;
  // Remote API includes group-device memberships on the GET /v1/group response
  devices?: GroupDevicePair[];
}

export interface CreateGroupData {
  name: string;
  daily_report_time: string;
}

export interface UpdateGroupData {
  name?: string;
  daily_report_time?: string;
}

class GroupsStoreClass {
  groups: Group[] = [];
  loading = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  /** Every device id currently assigned to any group. */
  get assignedDeviceIds(): Set<string> {
    const ids = new Set<string>();
    for (const group of this.groups) {
      for (const pair of group.devices ?? []) {
        ids.add(String(pair.device_id));
      }
    }
    return ids;
  }

  async fetchGroups(): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      const groups = await EmfitAPI.get<Group[]>(`${STATUS_API}/v1/group`);
      runInAction(() => {
        this.groups = groups || [];
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : 'Failed to fetch groups';
        this.loading = false;
      });
      console.error('Failed to fetch groups:', error);
    }
  }

  async createGroup(data: CreateGroupData): Promise<Group | null> {
    this.loading = true;
    this.error = null;

    try {
      const newGroup = await EmfitAPI.post<Group>(`${STATUS_API}/v1/group`, {
        body: JSON.stringify(data),
      });
      runInAction(() => {
        if (newGroup) {
          this.groups.push(newGroup);
        }
        this.loading = false;
      });
      return newGroup;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : 'Failed to create group';
        this.loading = false;
      });
      console.error('Failed to create group:', error);
      return null;
    }
  }

  async updateGroup(id: number, data: UpdateGroupData): Promise<boolean> {
    this.loading = true;
    this.error = null;

    try {
      const updatedGroup = await EmfitAPI.put<Group>(
        `${STATUS_API}/v1/group/${id}`,
        {
          body: JSON.stringify(data),
        }
      );
      runInAction(() => {
        if (updatedGroup) {
          const index = this.groups.findIndex((g) => g.id === id);
          if (index !== -1) {
            this.groups[index] = updatedGroup;
          }
        }
        this.loading = false;
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : 'Failed to update group';
        this.loading = false;
      });
      console.error('Failed to update group:', error);
      return false;
    }
  }

  async deleteGroup(id: number): Promise<boolean> {
    this.loading = true;
    this.error = null;

    try {
      await EmfitAPI.delete<void>(`${STATUS_API}/v1/group/${id}`);
      runInAction(() => {
        this.groups = this.groups.filter((g) => g.id !== id);
        this.loading = false;
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : 'Failed to delete group';
        this.loading = false;
      });
      console.error('Failed to delete group:', error);
      return false;
    }
  }

  getGroupById(id: number): Group | undefined {
    return this.groups.find((g) => g.id === id);
  }

  /**
   * Drops everything belonging to the current login. Called from
   * resetStores() on logout, so the next login starts empty.
   */
  reset(): void {
    this.groups = [];
    this.loading = false;
    this.error = null;
  }
}

export const GroupsStore = new GroupsStoreClass();
